import {
  MONTHLY_SPEND_CEILING_MAX_USD,
  MONTHLY_SPEND_CEILING_MIN_USD,
  RESEARCH_BUDGET_MIN_USD,
} from '@ni/shared';

/**
 * The spend-limit field's own decisions — reading a text field as an amount, and
 * saying when what is written is not one. The rest of the panel's form rules
 * live in `research.ts`, which holds this field alongside the depth selects it
 * is saved with.
 *
 * The control is the operator's since 2026-09-03 (Soroush): the engine checks a
 * run's cap **between rounds**, and a run can still overshoot by the wave of legs
 * that was already under way when the ceiling was crossed, plus the write-up that
 * follows — a budget stops new collection, never work in flight and never the
 * product. An operator may set a ceiling knowing
 * that; the office, which would reasonably read one as absolute, is shown no such
 * control at all — and since 2026-09-04 neither is anything else in this bag.
 *
 * `RESEARCH_BUDGET_MIN_USD` is imported as a **value**, unlike the type-only
 * import in `usage.ts`, and it is one number: the floor here has to be the
 * server's, or this app would offer a save the server refuses. One micro-dollar
 * is the smallest cap there is — the unit `ai_usage_event.cost_micro_usd` meters
 * in — and run creation rounds dollars into it, so anything smaller would land
 * on a cap of nothing.
 */

/**
 * The field's contents as an amount, or `null` for **no limit** — which is what
 * a blank field, and only a blank field, means.
 *
 * `null` is also what an unusable entry answers, which is why
 * {@link budgetUnusable} exists beside it: this function is asked what to
 * *send*, and the save is withheld before it is ever asked about a `-3`.
 */
export function budgetFrom(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const amount = Number(trimmed);
  return Number.isFinite(amount) && amount >= RESEARCH_BUDGET_MIN_USD ? amount : null;
}

/** Written, but not an amount: the save is withheld and the field says what to write. */
export function budgetUnusable(value: string): boolean {
  return value.trim().length > 0 && budgetFrom(value) === null;
}

/**
 * The stored limit as the field shows it: a blank for no limit, and the number
 * as it was written otherwise.
 *
 * `String(number)` rather than a currency format on purpose — this is an input's
 * value, not a rendered figure, and a formatted one would have to be parsed back
 * out of the field before it could be sent.
 */
export function budgetField(budgetUsd: number | null): string {
  return budgetUsd === null ? '' : String(budgetUsd);
}

/* ——————————————— the organization's month ——————————————— */

/**
 * **The month's allowance, as its own three functions** rather than a
 * parameterised version of the three above.
 *
 * They look like the same field and they are not the same rule: the per-run cap
 * floors at one micro-dollar because that is where "a cap" stops being
 * expressible, and this one floors at a cent and stops at a million because it is
 * a figure somebody writes on an invoice and a slipped keyboard is the only way
 * a seven-figure number gets typed. Folding them together would have meant one
 * of the two floors moving whenever the other did.
 *
 * The bounds are imported as values, for the reason the floor above is: they
 * have to be the server's, or this app offers a save the server refuses.
 */

/**
 * The field's contents as an allowance, or `null` for **no limit** — which is
 * what a blank field, and only a blank field, means.
 *
 * `null` is also what an unusable entry answers, which is why
 * {@link monthlyLimitUnusable} exists beside it: this function is asked what to
 * *send*, and the save is withheld before it is ever asked about a `-3`.
 *
 * The cents test is the server's, scaled rather than a modulo, because
 * `0.07 % 0.01` is not zero in IEEE 754.
 */
export function monthlyLimitFrom(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const amount = Number(trimmed);
  if (!Number.isFinite(amount)) return null;
  if (amount < MONTHLY_SPEND_CEILING_MIN_USD || amount > MONTHLY_SPEND_CEILING_MAX_USD) return null;
  return Math.abs(amount * 100 - Math.round(amount * 100)) < 1e-6 ? amount : null;
}

/** Written, but not an allowance: the save is withheld and the field says what to write. */
export function monthlyLimitUnusable(value: string): boolean {
  return value.trim().length > 0 && monthlyLimitFrom(value) === null;
}

/**
 * The stored limit as the field shows it: a blank for no limit, and the number
 * as it was written otherwise — `String(number)` rather than a currency format,
 * for the reason {@link budgetField} gives.
 */
export function monthlyLimitField(usd: number | null): string {
  return usd === null ? '' : String(usd);
}

/**
 * Whether there is no save to offer — the button's `disabled` and the save
 * function's own first line, as one rule rather than two copies of it. The same
 * three reasons {@link researchSaveWithheld} has, one field wide.
 */
export function monthlyLimitSaveWithheld(field: {
  draft: string;
  saved: string;
  saving: boolean;
}): boolean {
  if (field.saving) return true;
  if (monthlyLimitUnusable(field.draft)) return true;
  return monthlyLimitFrom(field.draft) === monthlyLimitFrom(field.saved);
}
